import { useNavigate, type NavigateFunction, useLocation } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import routes from "./config";
import { PATHS, type RouteKey, routeKeyFromPath } from "./paths";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

// Pro Route ein Titel je Sprache. Lookup per RouteKey, nicht per Pfad,
// damit DE- und EN-Slugs derselben Seite denselben Titel-Eintrag teilen.
const TITLES: Record<RouteKey, { de: string; en: string }> = {
  home: {
    de: 'Hotel Alpenhof Gauting | Hotel Starnberger See & nahe München seit 1911',
    en: 'Hotel Alpenhof Gauting | Hotel near Lake Starnberg & Munich since 1911',
  },
  rooms: {
    de: 'Zimmer & Preise | Hotel Alpenhof Gauting – Hotel nahe München & Starnberger See',
    en: 'Rooms & Rates | Hotel Alpenhof Gauting – Hotel near Munich & Lake Starnberg',
  },
  hotel: {
    de: 'Das Hotel | Hotel Alpenhof Gauting – Tradition & Gastfreundschaft seit 1911',
    en: 'The Hotel | Hotel Alpenhof Gauting – Tradition & Hospitality since 1911',
  },
  aufenthalt: {
    de: 'Ihr Aufenthalt | Hotel Alpenhof Gauting – Frühstück & Ambiente',
    en: 'Your stay | Hotel Alpenhof Gauting – Breakfast & atmosphere',
  },
  ausflugsziele: {
    de: 'Ausflugsziele | Hotel Alpenhof Gauting – Starnberger See, München & Alpen',
    en: 'Things to do | Hotel Alpenhof Gauting – Lake Starnberg, Munich & the Alps',
  },
  buchen: {
    de: 'Zimmer buchen | Hotel Alpenhof Gauting – Hotel am Starnberger See',
    en: 'Book a room | Hotel Alpenhof Gauting – Hotel near Lake Starnberg',
  },
  impressum:   { de: 'Impressum | Hotel Alpenhof Gauting',   en: 'Imprint | Hotel Alpenhof Gauting' },
  datenschutz: { de: 'Datenschutz | Hotel Alpenhof Gauting', en: 'Privacy | Hotel Alpenhof Gauting' },
  agb:         { de: 'AGB | Hotel Alpenhof Gauting',         en: 'Terms & Conditions | Hotel Alpenhof Gauting' },
};

const META_DE = 'Hotel Alpenhof Gauting – Ihr charmantes Hotel am Starnberger See, nur 25 Min. von München. Stilvolle Zimmer, Frühstück mit Bio-Produkten, kostenlose Parkplätze.';
const META_EN = 'Hotel Alpenhof Gauting – your charming hotel near Lake Starnberg, just 25 min from Munich. Stylish rooms, organic breakfast and free parking.';

const SEO_H1_DE = 'Hotel Alpenhof Gauting – Hotel am Starnberger See nahe München';
const SEO_H1_EN = 'Hotel Alpenhof Gauting – hotel near Lake Starnberg, close to Munich';

// Produktiv-Domain für canonical / hreflang. Vor Live-Gang ggf. anpassen.
const PROD_ORIGIN = 'https://www.alpenhof-gauting.de';

// Breadcrumb-Labels pro Sprache.
const BREADCRUMBS: Record<RouteKey, { de: string; en: string }> = {
  home:          { de: 'Startseite',    en: 'Home' },
  rooms:         { de: 'Zimmer & Preise', en: 'Rooms & Rates' },
  hotel:         { de: 'Das Hotel',     en: 'The Hotel' },
  aufenthalt:    { de: 'Ihr Aufenthalt', en: 'Your stay' },
  ausflugsziele: { de: 'Ausflugsziele', en: 'Things to do' },
  buchen:        { de: 'Buchen',        en: 'Book' },
  impressum:     { de: 'Impressum',     en: 'Imprint' },
  datenschutz:   { de: 'Datenschutz',   en: 'Privacy' },
  agb:           { de: 'AGB',           en: 'Terms' },
};

const BREADCRUMB_SCRIPT_ID = 'breadcrumb-jsonld';

function setBreadcrumb(key: RouteKey, lang: 'de' | 'en') {
  // Home selbst braucht keinen Crumb (wäre nur ein Element)
  const items = key === 'home'
    ? [{ '@type': 'ListItem', position: 1, name: BREADCRUMBS.home[lang], item: `${PROD_ORIGIN}${PATHS.home[lang]}` }]
    : [
        { '@type': 'ListItem', position: 1, name: BREADCRUMBS.home[lang], item: `${PROD_ORIGIN}${PATHS.home[lang]}` },
        { '@type': 'ListItem', position: 2, name: BREADCRUMBS[key][lang], item: `${PROD_ORIGIN}${PATHS[key][lang]}` },
      ];
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
  let script = document.getElementById(BREADCRUMB_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = BREADCRUMB_SCRIPT_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function setMeta(name: string, content: string) {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.setAttribute('content', content);
}

function setOg(prop: string, content: string) {
  const el = document.querySelector(`meta[property="${prop}"]`);
  if (el) el.setAttribute('content', content);
}

// hreflang/canonical-Link-Tags im <head> idempotent setzen.
function setLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selector = Object.entries(attrs).reduce(
    (sel, [k, v]) => `${sel}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  );
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearAlternates() {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
}

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Wenn die URL einen Slug enthält, der nur in einer Sprache existiert,
  // i18n entsprechend ausrichten (z. B. /rooms → en, /zimmer → de).
  useEffect(() => {
    const key = routeKeyFromPath(location.pathname);
    if (!key) return;
    const isEnglishSlug = PATHS[key].en === location.pathname && PATHS[key].de !== location.pathname;
    const isGermanSlug  = PATHS[key].de === location.pathname && PATHS[key].en !== location.pathname;
    if (isEnglishSlug && i18n.language !== 'en') i18n.changeLanguage('en');
    else if (isGermanSlug && i18n.language === 'en') i18n.changeLanguage('de');
  }, [location.pathname, i18n]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const lang: 'de' | 'en' = i18n.language === 'en' ? 'en' : 'de';
    const key = routeKeyFromPath(location.pathname);
    const fallbackTitle = lang === 'en'
      ? 'Hotel Alpenhof Gauting | Hotel near Lake Starnberg & Munich'
      : 'Hotel Alpenhof Gauting | Hotel Starnberger See & nahe München';
    document.title = key ? TITLES[key][lang] : fallbackTitle;
    document.documentElement.lang = lang;
    setMeta('description', lang === 'en' ? META_EN : META_DE);
    setMeta('content-language', lang);
    setOg('og:locale', lang === 'en' ? 'en_GB' : 'de_DE');
    setOg('og:title', document.title);
    setOg('og:description', lang === 'en' ? META_EN : META_DE);

    const seoH1 = document.getElementById('seo-h1');
    if (seoH1) seoH1.textContent = lang === 'en' ? SEO_H1_EN : SEO_H1_DE;

    // canonical + hreflang. Auf der Test-Domain (HashRouter) zeigen die URLs
    // noch nicht produktiv; sobald die Live-Domain mit BrowserRouter steht,
    // greift dieselbe Logik direkt korrekt.
    if (key) {
      const dePath = PATHS[key].de;
      const enPath = PATHS[key].en;
      const currentPath = lang === 'en' ? enPath : dePath;
      setLink('canonical', `${PROD_ORIGIN}${currentPath}`);

      clearAlternates();
      setLink('alternate', `${PROD_ORIGIN}${dePath}`,        { hreflang: 'de' });
      setLink('alternate', `${PROD_ORIGIN}${enPath}`,        { hreflang: 'en' });
      setLink('alternate', `${PROD_ORIGIN}${dePath}`,        { hreflang: 'x-default' });
      setOg('og:url', `${PROD_ORIGIN}${currentPath}`);

      setBreadcrumb(key, lang);
    }

    const timer = setTimeout(() => {
      if (typeof window !== 'undefined' && (window as any).dirs21) {
        (window as any).dirs21.rebindTriggers();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, i18n.language]);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  return element;
}
